import React, { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { TimezoneOption, getTimezoneStrings } from '@modernloop/shared/datetime';
import { CrossIcon, TimezoneIcon } from '@modernloop/shared/icons';
import { Autocomplete, Button, IconButton, Stack, TextField, Typography, createFilterOptions } from '@mui/material';
import { find } from 'lodash';

import { BaseProps } from 'src/components/types';

import {
  selectButtonLabelSxProps_DO_NOT_USE,
  selectButtonProps_DO_NOT_USE,
} from 'src/constants/selectButtonProps_DO_NOT_USE';

/**
 * Stable, non-translated identifier for the "recent timezones" group. Used as the
 * option `category` for grouping/equality logic; the displayed header is translated
 * at render time (see `groupBy`), so this value must never be localized.
 */
const RECENT_TIMEZONES_CATEGORY = 'Recent Timezones';

/**
 * Stable, non-translated identifier for the "all timezones" group, mirroring
 * {@link RECENT_TIMEZONES_CATEGORY}. Set in `getTimezoneStrings`; translated for
 * display only (see `groupBy`).
 */
const ALL_TIMEZONES_CATEGORY = 'All Timezones';

export type BaseTimezoneSelectProps = BaseProps & {
  className?: string;
  alwaysEditMode?: boolean;
  label?: string | React.ReactNode;
  value?: string; // tz

  /**
   * If true, the input will take up the full width of its container.
   */
  fullWidth?: boolean;

  /**
   * If true, the select button will take up the full width of its container.
   */
  buttonFullWidth?: boolean;
  disableClearable?: boolean;
  disabled?: boolean;
  openOnFocus?: boolean;
  lastUsedTimezones: string[];
  onRemoveRecentTimezone?: (timezone: string) => void;
  additionalTimezones?: { [key: string]: string[] };
  getTimezoneLabel?: (timezone) => string;
  onChange?: (tz: string) => void;
  overrideCandidateTimezoneReadClass?: string;
};

const autocompleteRootSx = {
  textOverflow: 'ellipsis',
  border: (theme) => `1px solid ${theme.palette.border}`,
  borderRadius: '6px',
  padding: '0 8px',
  '& .MuiInput-underline:before': {
    border: 'none',
  },
  '&:hover .MuiInput-underline:before': {
    border: 'none',
  },
  '& .MuiInput-underline:after': {
    border: 'none',
  },
  '&:hover .MuiInput-underline:after': {
    border: 'none',
  },
} as const;

const candidateTimezoneReadSx = {
  maxWidth: '100%',
  padding: '0 8px',
  borderRadius: '6px',
  fontWeight: (theme) => theme.typography.fontWeightRegular,
} as const;

/** Timezone picker with recent selections, grouped options, and read/edit modes. */
const BaseTimezoneSelect: FC<BaseTimezoneSelectProps> = ({
  className,
  dataTestId,
  alwaysEditMode,
  label,
  value,
  fullWidth,
  buttonFullWidth,
  disabled,
  disableClearable,
  openOnFocus,
  lastUsedTimezones,
  onRemoveRecentTimezone,
  additionalTimezones,
  getTimezoneLabel,
  onChange,
  overrideCandidateTimezoneReadClass,
}) => {
  const [editMode, setEditMode] = useState(false);
  const { t } = useTranslation();
  const timezoneStrings = useMemo(() => getTimezoneStrings(t), [t]);

  const autcompleteProps = {
    className,
    fullWidth,
    disableClearable,
    disabled,
  };

  const getTimezoneOption = (timezone: string, category: string): TimezoneOption | null => {
    const tzOption = timezoneStrings.find((option) => option.value === timezone);
    if (!tzOption) {
      return null;
    }
    const result: TimezoneOption = { ...tzOption, category };
    return result;
  };

  // Create the time options for all the last used timezones
  const recentTzs: TimezoneOption[] = [];

  if (additionalTimezones) {
    Object.keys(additionalTimezones).forEach((key) => {
      const timezones = additionalTimezones[key];
      if (timezones && timezones.length) {
        timezones.forEach((timezone) => {
          const tzOption = getTimezoneOption(timezone, key);
          if (!tzOption) {
            return;
          }
          recentTzs.push(tzOption);
        });
      }
    });
  }

  lastUsedTimezones.forEach((lastUsedTimezone) => {
    const tzOption = getTimezoneOption(lastUsedTimezone, RECENT_TIMEZONES_CATEGORY);
    if (!tzOption) {
      return;
    }
    recentTzs.push(tzOption);
  });

  const options = recentTzs.concat(timezoneStrings);

  const getOptionLabel = (option: TimezoneOption) => option.label;

  // Translate the group header for display only — the underlying category stays a
  // stable identifier so grouping and equality checks remain locale-independent.
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case RECENT_TIMEZONES_CATEGORY:
        return t('hiring-dashboard-common:common.timezone.recentTimezones');
      case ALL_TIMEZONES_CATEGORY:
        return t('hiring-dashboard-common:common.timezone.allTimezones');
      default:
        return category;
    }
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: TimezoneOption) => {
    const isRecentTimezone = option.category === RECENT_TIMEZONES_CATEGORY;
    if (isRecentTimezone && onRemoveRecentTimezone) {
      return (
        <li
          {...props}
          data-testid="timezone-option"
          data-timezone={option.value}
          data-timezone-category={option.category}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography noWrap>{option.label}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveRecentTimezone(option.value);
              }}
              sx={{ ml: 1, p: 0.25 }}
            >
              <CrossIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </li>
      );
    }
    return (
      <li
        {...props}
        data-testid="timezone-option"
        data-timezone={option.value}
        data-timezone-category={option.category}
      >
        <Typography noWrap>{option.label}</Typography>
      </li>
    );
  };

  const getOptionFromValue = (val: string | undefined) => {
    return find(options, { value: val });
  };

  const handleChange = (event, option: TimezoneOption) => {
    if (onChange && option && option.value) {
      onChange(option.value);
      setEditMode(false);
    }
  };

  const filterOptions = createFilterOptions({
    stringify: (tz: TimezoneOption) => `${tz.label},${tz.value},${tz.keywords}`,
  });

  const handleBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const timeOption = options.find((option) => option.value === event.target.value);
      const currentValue = find(options, { value })?.value;
      const newValue = timeOption ? timeOption.value : currentValue;
      setEditMode(false);

      if (onChange && newValue && newValue !== currentValue) {
        onChange(newValue);
      }
    },
    [onChange, options, value]
  );

  if (!alwaysEditMode && !editMode) {
    return (
      <Button
        {...selectButtonProps_DO_NOT_USE}
        fullWidth={buttonFullWidth}
        disabled={disabled}
        className={overrideCandidateTimezoneReadClass}
        data-testid={dataTestId}
        startIcon={<TimezoneIcon color={disabled ? 'disabled' : 'inherit'} />}
        onClick={() => setEditMode(true)}
        sx={{ ...selectButtonProps_DO_NOT_USE.sx, ...candidateTimezoneReadSx, minWidth: 280 }}
      >
        <Typography sx={selectButtonLabelSxProps_DO_NOT_USE}>
          {getTimezoneLabel ? getTimezoneLabel(value) : (getOptionFromValue(value)?.label ?? value)}
        </Typography>
      </Button>
    );
  }

  return (
    <Autocomplete
      {...autcompleteProps}
      openOnFocus
      selectOnFocus
      open={openOnFocus}
      sx={autocompleteRootSx}
      style={fullWidth ? undefined : { width: 300 }}
      options={options}
      groupBy={(option) => getCategoryLabel(option.category)}
      value={getOptionFromValue(value)}
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      renderOption={renderOption}
      filterOptions={filterOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            'data-testid': dataTestId ? `${dataTestId}-input` : undefined,
          }}
          onFocus={(event) => {
            const input = event.currentTarget;
            window.setTimeout(() => input.select(), 0);
          }}
          autoFocus={!alwaysEditMode}
          data-testid={dataTestId}
          onBlur={handleBlur}
          label={label}
          fullWidth
          variant="standard"
        />
      )}
    />
  );
};

export default BaseTimezoneSelect;
